/**
 * Replacement planning — the plan list (the `תכנון החלפה` tab on the
 * inventory screen).
 *
 * Functional spec: `docs/features/REPLACEMENT-PLANNING.md` (UI section). This
 * feature has no Qt screen behind it — web-only, so there is no `qt_poc/*.py`
 * to mirror, unlike every other screen in this app.
 *
 * `GET /replacement-plans` returns the whole computed view per plan (totals,
 * device counts included) rather than bare headers, because the list shows
 * totals and there is no cheaper way to have them — see the router's own
 * docstring on why that is an accepted O(N) at this feature's scale.
 */
import { useMemo, useState } from 'react'
import {
  Alert, Button, Group, Paper, Stack, Text, TextInput, Title,
} from '@mantine/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { useNavigate } from 'react-router-dom'
import type { ColumnDef } from '@tanstack/react-table'

import { ApiError, api } from '../api/client'
import type {
  InventorySnapshot, MasterData, PlanComputed, PlanStatus, ReplacementPlanInput,
} from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { Modal } from '../components/AppModal'
import { DataGrid } from '../components/DataGrid'
import { HiddenAmount } from '../components/HiddenAmount'
import { StatusBadge } from '../components/StatusBadge'
import { EM_DASH, fmtDateTime, fmtMoney, fmtQty } from '../format'
import { FILL_PANEL } from '../theme'

/** English status codes (stored, like an issuance's) -> the Hebrew word the
 * design calls for. Shared with `ReplacementPlanEditor.tsx` so the list badge
 * and the editor's header badge never drift apart. `StatusBadge` still keys
 * its colour off the stored English code (see that component's `label` prop
 * doc) — this map only ever supplies the displayed word. */
export const PLAN_STATUS_LABEL_HE: Record<PlanStatus, string> = {
  DRAFT: 'טיוטה',
  APPROVED: 'מאושר',
  CONVERTED: 'הומר להזמנה',
  CANCELLED: 'בוטל',
}

function blankPlanInput(name: string): ReplacementPlanInput {
  return {
    name, snapshot_id: null, dealer_id: null, customer_id: null,
    budget_id: null, currency: 'USD', vat_pct: 17, notes: '', lines: [],
  }
}

export function ReplacementPlanList() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { canEdit } = useAuth()
  const [creating, setCreating] = useState(false)

  const plans = useQuery({
    queryKey: ['replacement-plans'],
    queryFn: () => api.get<PlanComputed[]>('/replacement-plans'),
  })
  const snapshots = useQuery({
    queryKey: ['snapshots'],
    queryFn: () => api.get<InventorySnapshot[]>('/inventory/snapshots'),
  })
  const master = useQuery({
    queryKey: ['master-data'],
    queryFn: () => api.get<MasterData>('/master-data'),
  })

  const snapshotName = useMemo(() => {
    const map = new Map<number, string>()
    for (const s of snapshots.data ?? []) if (s.id != null) map.set(s.id, s.snapshot_name)
    return map
  }, [snapshots.data])
  const dealerName = useMemo(() => {
    const map = new Map<number, string>()
    for (const d of master.data?.dealers ?? []) if (d.id != null) map.set(d.id, d.name)
    return map
  }, [master.data])

  const columns = useMemo<ColumnDef<PlanComputed, any>[]>(
    () => [
      { id: 'name', header: 'שם', accessorFn: (r) => r.plan.name },
      {
        id: 'status',
        header: 'סטטוס',
        meta: { nowrap: true },
        cell: (c) => (
          <StatusBadge
            value={c.row.original.plan.status}
            label={PLAN_STATUS_LABEL_HE[c.row.original.plan.status as PlanStatus]}
          />
        ),
      },
      {
        id: 'snapshot',
        header: 'צילום מלאי',
        meta: { hideBelow: 'lg' },
        cell: (c) => {
          const id = c.row.original.plan.snapshot_id
          return id == null ? 'ללא מלאי' : snapshotName.get(id) ?? EM_DASH
        },
      },
      {
        id: 'dealer',
        header: 'דילר',
        meta: { hideBelow: 'lg' },
        cell: (c) => {
          const id = c.row.original.plan.dealer_id
          return id == null ? EM_DASH : dealerName.get(id) ?? EM_DASH
        },
      },
      {
        id: 'replaced',
        header: 'מכשירים להחלפה',
        meta: { numeric: true, hideBelow: 'md' },
        accessorFn: (r) => r.devices_replaced,
        cell: (c) => fmtQty(c.getValue<number>()),
      },
      {
        id: 'from_stock',
        header: 'מהמחסן',
        meta: { numeric: true, hideBelow: 'md' },
        accessorFn: (r) => r.devices_from_stock,
        cell: (c) => fmtQty(c.getValue<number>()),
      },
      {
        id: 'to_order',
        header: 'להזמנה',
        meta: { numeric: true, hideBelow: 'md' },
        accessorFn: (r) => r.devices_to_order,
        cell: (c) => fmtQty(c.getValue<number>()),
      },
      {
        id: 'total',
        header: 'סה״כ',
        meta: { numeric: true },
        cell: (c) => {
          const row = c.row.original
          return row.total == null
            ? <HiddenAmount />
            : fmtMoney(row.total, row.plan.currency)
        },
      },
      {
        id: 'updated',
        header: 'עודכן',
        meta: { hideBelow: 'lg' },
        cell: (c) => fmtDateTime(c.row.original.plan.updated_at),
      },
    ],
    [snapshotName, dealerName],
  )

  return (
    <Stack gap="md" h="100%" style={{ minHeight: 0 }}>
      <Group justify="space-between">
        <Title order={4}>תוכניות החלפה</Title>
        {canEdit && <Button onClick={() => setCreating(true)}>תוכנית חדשה</Button>}
      </Group>

      <Paper withBorder style={FILL_PANEL}>
        <DataGrid
          data={plans.data ?? []}
          columns={columns}
          loading={plans.isLoading}
          empty={'אין תוכניות החלפה. השתמש ב"תוכנית חדשה" כדי להתחיל.'}
          onRowClick={(row) => navigate(`/inventory/plans/${row.plan.id}`)}
          maxHeight="100%"
        />
      </Paper>

      <NewPlanModal
        opened={creating}
        onClose={() => setCreating(false)}
        onCreated={(id) => {
          setCreating(false)
          void qc.invalidateQueries({ queryKey: ['replacement-plans'] })
          navigate(`/inventory/plans/${id}`)
        }}
      />
    </Stack>
  )
}

/** The plan needs only a name to exist — everything else (snapshot, dealer,
 * lines…) is filled in on the editor page itself, the same "create nearly
 * empty, then edit" shape `BomEditorModal`'s create path has. */
function NewPlanModal({
  opened, onClose, onCreated,
}: { opened: boolean; onClose: () => void; onCreated: (id: number) => void }) {
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: (value: string) =>
      api.post<PlanComputed>('/replacement-plans', blankPlanInput(value)),
    onSuccess: (created) => {
      setName('')
      setError(null)
      notifications.show({ message: 'התוכנית נוצרה', color: 'green' })
      onCreated(created.plan.id!)
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'יצירת התוכנית נכשלה.'),
  })

  function submit() {
    if (!name.trim()) return setError('יש להזין שם לתוכנית.')
    create.mutate(name.trim())
  }

  function close() {
    setName('')
    setError(null)
    onClose()
  }

  return (
    <Modal opened={opened} onClose={close} title="תוכנית החלפה חדשה">
      <form onSubmit={(e) => { e.preventDefault(); submit() }}>
        <Stack>
          {error && <Alert color="red" variant="light">{error}</Alert>}
          <TextInput
            label="שם התוכנית"
            required
            autoFocus
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
          <Text size="xs" c="dimmed">
            צילום מלאי, דילר, לקוח ותקציב נבחרים בעמוד התוכנית עצמו.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={close}>ביטול</Button>
            <Button type="submit" loading={create.isPending}>המשך</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}
